/**
 * Identité de l'opérateur — écran repris de la version du client (itération 30).
 *
 * Ces informations partent sur les mandats et les factures. Trois d'entre elles sont
 * indispensables — raison sociale, SIRET, adresse — et le serveur signale leur absence
 * plutôt que de la taire : une facture sans SIRET n'identifie pas son émetteur.
 *
 * L'enregistrement fusionne avec l'existant : l'écran envoie parfois un seul champ, et
 * remplacer tout l'objet effacerait le reste sans que personne ne l'ait demandé.
 *
 * Seuls les chemins d'import changent par rapport à son fichier.
 */
import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/shared/auth/AuthContext';
import { useOperator } from '@/shared/auth/OperatorContext';
import api from '@/shared/api/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { Label } from '@/shared/ui/label';
import { Separator } from '@/shared/ui/separator';
import { toast } from 'sonner';
import { Building2, Upload, Save, Image as ImageIcon, Trash2 } from 'lucide-react';

// Les champs, groupés par ce à quoi ils servent.
//
// ⚠️ La deuxième et la troisième famille ont été ajoutées le 27/08 (lot H). Elles ne
// servent qu'aux FACTURES, mais elles décrivent l'entreprise et pas un document : les
// saisir une fois vaut pour toutes les factures à venir. `tva_num` existait côté serveur
// depuis le début et n'avait jamais eu de champ à l'écran — une facture sans numéro de
// TVA intracommunautaire ne permet pas à l'acheteur de déduire la TVA.
const GROUPES = [
  {
    titre: 'Identification',
    aide: 'Figure en tête des mandats, attestations et factures.',
    champs: [
      { key: 'raison_sociale', label: 'Raison sociale', placeholder: 'Ex. Ecoflexibilité SAS' },
      { key: 'siret', label: 'SIRET', placeholder: '12345678900012' },
      { key: 'tva_num', label: 'N° TVA intracommunautaire', placeholder: 'FR62901234567' },
      { key: 'adresse', label: 'Adresse', placeholder: '12 rue de la Flexibilité', large: true },
      { key: 'code_postal', label: 'Code postal', placeholder: '75001' },
      { key: 'ville', label: 'Ville', placeholder: 'Paris' },
      { key: 'telephone', label: 'Téléphone', placeholder: '+33 1 23 45 67 89' },
      { key: 'email_support', label: 'Email de support', placeholder: 'contact@ecoflexibilite.fr' },
      { key: 'site_web', label: 'Site web', placeholder: 'https://ecoflexibilite.fr' },
    ],
  },
  {
    titre: 'Mentions légales des factures',
    aide: 'Obligatoires sur une facture entre professionnels (art. R123-237 du code de commerce).',
    champs: [
      { key: 'forme_juridique', label: 'Forme juridique', placeholder: 'SAS' },
      { key: 'capital_social', label: 'Capital social', placeholder: '10 000 €' },
      { key: 'rcs', label: 'Immatriculation RCS', placeholder: 'RCS Nanterre 901 234 567', large: true },
    ],
  },
  {
    titre: 'Règlement',
    aide: 'Laissés vides, les deux derniers champs sont remplacés sur la facture par les mentions du code de commerce. Ils ne peuvent donc pas disparaître du document.',
    champs: [
      { key: 'iban', label: 'IBAN', placeholder: 'FR76 3000 4000 0312 3456 7890 143', large: true },
      { key: 'bic', label: 'BIC', placeholder: 'BNPAFRPPXXX' },
      { key: 'conditions_reglement', label: 'Conditions de règlement',
        placeholder: 'Paiement par virement sous 30 jours.', large: true, long: true },
      { key: 'penalites_retard', label: 'Pénalités de retard',
        placeholder: 'Trois fois le taux d\'intérêt légal en vigueur.', large: true, long: true },
    ],
  },
];

const MAX_LOGO_KB = 500;

export default function IdentitePage() {
  const { user } = useAuth();
  const { operator, refresh } = useOperator();
  const [branding, setBranding] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (operator?.branding) {
      setBranding({ ...operator.branding });
      setLogoPreview(operator.branding.logo_url || '');
    }
  }, [operator]);

  if (user?.role !== 'admin') {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="p-6">Accès réservé aux administrateurs.</CardContent>
        </Card>
      </div>
    );
  }

  if (!branding) {
    return <div className="p-8 text-neutral-500 text-sm">Chargement…</div>;
  }

  const handleField = (k, v) => setBranding((b) => ({ ...b, [k]: v }));

  const handleLogoPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Format image requis (PNG, JPG, WebP)');
      return;
    }
    if (f.size > MAX_LOGO_KB * 1024) {
      toast.error(`Fichier trop volumineux (max ${MAX_LOGO_KB} Ko)`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      setLogoPreview(dataUrl);
      setBranding((b) => ({ ...b, logo_url: dataUrl }));
    };
    reader.readAsDataURL(f);
  };

  const removeLogo = () => {
    setLogoPreview('');
    setBranding((b) => ({ ...b, logo_url: '' }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/operator/branding', branding);
      toast.success('Identité mise à jour');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6" data-testid="identite-page">
      {/* Le badge du coin haut-droit a ete retire le 02/09/2026.
          Il affichait `tenants.name`, dont la valeur par defaut est la CONSTANTE
          DEFAULT_TENANT_NAME (core/tenant.py) — pas la raison sociale saisie dans le
          formulaire juste en dessous. Il montrait donc « EcoFlexibilite » a un
          operateur qui venait d'ecrire son propre nom a dix centimetres de la, et
          rien ne disait lequel des deux faisait foi. Un badge qui affirme un nom que
          personne n'a saisi vaut moins que pas de badge du tout. */}
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Identité de l&apos;entreprise</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Personnalisez le nom, le logo et les informations affichées à vos utilisateurs et sur les documents générés (mandats, attestations, factures).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logo */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Logo
            </CardTitle>
            <CardDescription>PNG, JPG ou WebP. Max {MAX_LOGO_KB} Ko.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="border-2 border-dashed border-neutral-200 rounded-md h-40 flex items-center justify-center bg-neutral-50 overflow-hidden">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo opérateur" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
              ) : (
                <div className="text-center text-neutral-400 text-sm">
                  <Building2 className="w-8 h-8 mx-auto mb-1 opacity-40" />
                  Aucun logo
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoPick} className="hidden" data-testid="logo-file-input" />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="flex-1" data-testid="logo-upload-btn">
                <Upload className="w-3.5 h-3.5 mr-1.5" /> {logoPreview ? 'Changer' : 'Ajouter'}
              </Button>
              {logoPreview && (
                <Button variant="ghost" size="sm" onClick={removeLogo} className="text-red-600 hover:text-red-700" data-testid="logo-remove-btn">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Formulaire */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Informations légales et contact</CardTitle>
            <CardDescription>Ces informations apparaissent sur les PDFs générés pour vos clients.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {GROUPES.map((groupe, index) => (
              <div key={groupe.titre} className="space-y-3">
                {index > 0 && <Separator />}
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">{groupe.titre}</p>
                  <p className="text-[11.5px] text-neutral-400 mt-0.5">{groupe.aide}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {groupe.champs.map((f) => (
                    <div key={f.key} className={f.large ? 'md:col-span-2' : ''}>
                      <Label htmlFor={`f-${f.key}`} className="text-xs">{f.label}</Label>
                      {f.long ? (
                        <Textarea
                          id={`f-${f.key}`}
                          rows={2}
                          value={branding[f.key] || ''}
                          placeholder={f.placeholder}
                          onChange={(e) => handleField(f.key, e.target.value)}
                          className="mt-1"
                          data-testid={`identite-${f.key}`}
                        />
                      ) : (
                        <Input
                          id={`f-${f.key}`}
                          value={branding[f.key] || ''}
                          placeholder={f.placeholder}
                          onChange={(e) => handleField(f.key, e.target.value)}
                          className="mt-1"
                          data-testid={`identite-${f.key}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <Separator />
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="identite-save-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
